import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import { OrderFields, PriorityId } from 'types/order'
import { Feature } from 'types/user'

import { selectOrderWrite, selectOrderFormErrors } from 'store/order/order.selectors'

import { selectSelectLists } from 'store/selectLists/selectLists.selectors'
import { selectCurrentUser } from 'store/users/users.selectors'

import SelectControl from 'components/molecules/SelectControl'
import TextareaControl from 'components/molecules/TextareaControl'
import Title from 'components/atoms/Title'

import { createOrderCameViaReplaceStorageKey, createOrderPath, whatWeCheckUrl } from 'routes/url-constants'

import { OrderProps } from './index'
import OrderTypes from './OrderTypes'

const orderTypeInfoBannerDismissedKey = 'orders-fe:orderTypeInfoBanner:dismissed'

const Customer: React.FC<OrderProps> = ({ onChange }) => {
  const history = useHistory()
  const location = useLocation()
  const currentUser = useSelector(selectCurrentUser)
  const form = useSelector(selectOrderWrite)
  const errors = useSelector(selectOrderFormErrors)
  const selectLists = useSelector(selectSelectLists)
  const [isInfoBannerDismissed, setInfoBannerDismissed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(orderTypeInfoBannerDismissedKey) === '1'
    } catch {
      return false
    }
  })

  const types = useMemo(
    () => OrderTypes.getForExternalUser(currentUser?.features?.[Feature.AVAILABLE_ORDER_TYPES] || null, selectLists?.orderType || []),
    [currentUser, selectLists],
  )

  const allowPriority = currentUser?.features?.[Feature.ALLOW_PRIORITY]
  const isPhoneInvitation = currentUser?.features?.[Feature.CREATED_VIA] === 'phone-invitation'

  const isCreateOrderPage = location.pathname === createOrderPath

  const dismissInfoBanner = useCallback(() => {
    try {
      window.localStorage.setItem(orderTypeInfoBannerDismissedKey, '1')
    } catch {
      // ignore
    }
    setInfoBannerDismissed(true)
  }, [])

  useEffect(() => {
    if (!isCreateOrderPage) return
    if (history.action === 'PUSH') {
      sessionStorage.removeItem(createOrderCameViaReplaceStorageKey)
    } else if (history.action === 'REPLACE') {
      sessionStorage.setItem(createOrderCameViaReplaceStorageKey, '1')
    }
  }, [isCreateOrderPage, history.action])

  const wasRedirectToCreateOrder =
    history.action === 'REPLACE' || (isCreateOrderPage && sessionStorage.getItem(createOrderCameViaReplaceStorageKey) === '1')

  return (
    <>
      {form[OrderFields.ORDER_TYPE] && !isInfoBannerDismissed ? (
        <div style={{ margin: '4px -16px' }}>
          <div role="alert" className="fade m-0 alert alert-info show">
            <div>
              Посмотрите, пожалуйста,{' '}
              <a href={whatWeCheckUrl} target="_blank" rel="noopener noreferrer">
                что мы проверяем
              </a>
              . Это важно!
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={dismissInfoBanner}>
              Понятно
            </button>
          </div>
        </div>
      ) : null}
      <Title text="Заявка" />
      {!errors[OrderFields.ORDER_TYPE].isValid ? (
        <div style={{ margin: '4px -16px' }}>
          <div role="alert" className="fade m-0 alert alert-warning show">
            Внимание!
            <br />
            Выберете тип заявки, и появится следующая часть формы.
          </div>
        </div>
      ) : null}
      <ul className="list-group needs-validation">
        <li className="list-group-item row">
          <div className="p-0">
            <SelectControl
              value={form[OrderFields.ORDER_TYPE]}
              isValid={errors[OrderFields.ORDER_TYPE].isValid}
              invalidMessage={errors[OrderFields.ORDER_TYPE].invalidMessage}
              id={OrderFields.ORDER_TYPE}
              options={types}
              onChangeHandler={onChange}
              className="col-lg-4 col-md-6 col-sm-12"
              label="Тип заявки:"
              withEmptyValue
            />
          </div>
        </li>
        <li className="list-group-item row">
          <div className="p-0">
            <SelectControl
              value={form[OrderFields.PRIORITY]}
              isValid={errors[OrderFields.PRIORITY].isValid}
              invalidMessage={errors[OrderFields.PRIORITY].invalidMessage}
              id={OrderFields.PRIORITY}
              options={selectLists?.priority}
              onChangeHandler={onChange}
              className="col-lg-4 col-md-6 col-sm-12"
              label="Приоритет заявки:"
              disabled={!allowPriority}
            />
            {!allowPriority && !wasRedirectToCreateOrder && !isPhoneInvitation && (
              <div className="small text-danger mt-1">
                Функционал изменение приоритета заявки вам недоступен.
                <br />
                Сейчас вы можете создавать заявки только с обычным приоритетом.
                <br />
                Запросите доступ на создание срочных заявок у своего руководства.
                <br />
                Стоимость создания срочной заявки +500р к цене заявки. Время рассмотрения срочных заявок до 15 минут.
              </div>
            )}
            {allowPriority && String(form[OrderFields.PRIORITY]) === PriorityId.URGENTLY && (
              <div className="small text-secondary mt-1">Стоимость создания срочной заявки +500р к цене заявки.</div>
            )}
          </div>
        </li>
        {form[OrderFields.ORDER_TYPE] ? (
          <li className="list-group-item row">
            <div className="p-0">
              <TextareaControl
                value={form[OrderFields.ORDER_TEXT]}
                isValid={errors[OrderFields.ORDER_TEXT].isValid}
                invalidMessage={errors[OrderFields.ORDER_TEXT].invalidMessage}
                id={OrderFields.ORDER_TEXT}
                onChangeHandler={onChange}
                label="Примечания:"
                rows={6}
              />
            </div>
          </li>
        ) : null}
      </ul>
    </>
  )
}

export default Customer
